import { getFindPeopleContext } from '../../../shared';
import { QueryData, QuerySource } from '../../models/query';
import type {
    FindPeopleRequest,
    TargetFolderId,
    IndexedPageView,
    PropertyUri,
    PersonaResponseShape,
    TFactories,
} from '../../../service';

function getDefaultPageView(maxResults: number, serviceFactory: TFactories): IndexedPageView {
    return serviceFactory.indexedPageView({
        BasePoint: 'Beginning',
        Offset: 0,
        MaxEntriesReturned: maxResults || 150,
    });
}

const defaultPropertyUri: PropertyUri = {
    FieldURI: 'PersonaAttributions',
};

const relevanceScoreUri: PropertyUri = {
    FieldURI: 'PersonaRelevanceScore',
};

const jobTitleUri: PropertyUri = {
    FieldURI: 'PersonaTitle',
};

const getDefaultShape = (serviceFactory: TFactories) => {
    const defaultShape: PersonaResponseShape = {
        BaseShape: 'Default',
        AdditionalProperties: [
            serviceFactory.propertyUri(defaultPropertyUri),
            serviceFactory.propertyUri(relevanceScoreUri),
            serviceFactory.propertyUri(jobTitleUri),
        ],
    };
    return defaultShape;
};

function getParentFolderId(
    queryData: QueryData,
    searchRecipientCache: boolean,
    serviceFactory: TFactories
): TargetFolderId {
    switch (queryData.source) {
        case QuerySource.LegacyFederatedSearch:
        case QuerySource.Directory:
            return undefined;

        default:
            return queryData.internalFolderId && !searchRecipientCache
                ? serviceFactory.targetFolderId({
                      BaseFolderId: serviceFactory.folderId({ Id: queryData.internalFolderId }),
                  })
                : undefined;
    }
}

function getQuerySources(queryData: QueryData): string[] {
    switch (queryData.source) {
        case QuerySource.LegacyFederatedSearch:
        case QuerySource.Directory:
        case QuerySource.RecipientCacheSuggestions:
            return ['Mailbox', 'Directory'];

        default:
            return ['Mailbox'];
    }
}

export function getFindPeopleRequest(
    serviceFactory: TFactories,
    queryData: QueryData,
    searchRecipientCache: boolean = false,
    maxResults?: number
): FindPeopleRequest {
    let findPeopleRequest: FindPeopleRequest = {
        IndexedPageItemView: getDefaultPageView(maxResults, serviceFactory),
        PersonaShape: serviceFactory.personaResponseShape(getDefaultShape(serviceFactory)),
        ShouldResolveOneOffEmailAddress: false,
        QueryString: queryData.currentQueryString,
        SearchPeopleSuggestionIndex: searchRecipientCache,
        Context: getFindPeopleContext(),
        QuerySources: getQuerySources(queryData),
    };

    const parentFolderId = getParentFolderId(queryData, searchRecipientCache, serviceFactory);
    if (parentFolderId) {
        findPeopleRequest = {
            ...findPeopleRequest,
            ParentFolderId: parentFolderId,
        };
    }

    return findPeopleRequest;
}
